import React, {useCallback, useEffect} from "react"
import Grid from "@mui/material/Grid2";
import {Button, CircularProgress, Typography} from "@mui/material";
import {a11yClick} from "../../../Utils/a11yHelpers";
import {formatPrice, getServiceTranslation} from "../../../Utils/helpers";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../Redux";
import {CallbackArray} from "../../../Utils/types";
import ReservationActions from "../../../Redux/reservationRedux";
import {InspectService} from "../../../interfaces";
import {useTranslation} from "react-i18next";

interface ShowServicesProps {
    isDriveIn: boolean
    service: InspectService
    setService: (item: InspectService) => void
    campaignCode: string
    serviceId?: number
    disabled: boolean
}

const ShowServices = ({isDriveIn, service, setService, campaignCode, serviceId, disabled}: ShowServicesProps) => {
    const serviceDirection = 'column'
    const containerDirection = 'row'

    const { t } = useTranslation()
    const dispatch = useDispatch()

    const registerInfo = useSelector((state: RootState) => state.reservation.registerInfo)
    const services = useSelector((state: RootState) => state.reservation.services)
    const chainId = useSelector((state: RootState) => state.ui.settings.chain.id)
    const stationId = useSelector((state: RootState) => state.ui.settings.station.id)
    const customerInfo = useSelector((state: RootState) => state.ui.customer)

    const getServicePrice = useCallback((service: InspectService): number | null => {
        if (service.id === registerInfo?.afterInspectionProductId && registerInfo?.afterInspectionPrice?.value) {
            return registerInfo?.afterInspectionPrice?.value ?? null
        } else {
            const basePrice = (isDriveIn ? null : service.lowestPrice?.value) ?? service.listPrice.value ?? null

            return basePrice === null ? null : (basePrice + (service.additionalFee?.value ?? 0))
        }
    }, [isDriveIn, registerInfo])

    useEffect(() => {
        const handleGetServices = (
            callback?: () => void | CallbackArray,
            campaign?: string | null,
        ) => {
            const station = {
                id: stationId
            }
            const callbacks = callback && Array.isArray(callback) ? callback : (callback ? [callback] : undefined)
            dispatch(ReservationActions.getServices(chainId, station, campaign, isDriveIn ? 1 : undefined, customerInfo?.token, callbacks))
        }

        if (registerInfo) {
            handleGetServices(undefined, campaignCode)
        }
    }, [campaignCode, chainId, stationId, registerInfo, isDriveIn, customerInfo, dispatch]);

    useEffect(() => {
        if (services && services.length > 0) {
            if (serviceId) {
                const service = services.find(service => service.id === serviceId)
                setService(service || services[0])
            } else {
                const service = services.find(service => service.isDefault === true)
                setService(service || services[0])
            }
        }
    }, [services, serviceId, setService]);

    if (! registerInfo) {
        return null
    }

    return (<>
        {!services && <Grid style={{textAlign: 'center'}}>
            <CircularProgress size={48} color="primary" />
        </Grid>}
        {services && services.length === 0 ?
            <Grid style={{textAlign: 'center'}}>
                <Typography variant="subtitle1">{t('selfService.noServicesNotice')}</Typography>
            </Grid>
            :
            <Grid container wrap="nowrap" direction={containerDirection} spacing={8} minHeight={'75%'}>
                <Grid style={{minWidth: '40%'}}>
                    <Grid container wrap="nowrap" direction={serviceDirection} spacing={2}>
                        {services?.map((item) => (
                            <Grid key={item.id}>
                                <Button
                                    size="small"
                                    fullWidth
                                    className={service?.id === item.id ? 'selectedServiceButton' : 'serviceButton'}
                                    variant="contained"
                                    disabled={disabled}
                                    color={"primary"}
                                    {...a11yClick(() => setService(item))}
                                >
                                    {getServiceTranslation('name', item)}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid>
                    {service && <Grid container wrap="nowrap" direction={serviceDirection} justifyContent="space-between" spacing={4}>
                        <Grid>
                            <Typography variant="h4" gutterBottom>
                                {getServiceTranslation('name', service)}
                            </Typography>
                            <Typography maxWidth={'90%'}>{getServiceTranslation('additionalInfo', service)}</Typography>
                        </Grid>
                        <Grid>
                            <Typography variant="h3" gutterBottom>
                                {isDriveIn || customerInfo?.isInvoicingEnabled ? '' : t('selfService.priceFrom')} {formatPrice(getServicePrice(service), 'EUR', 0)}
                            </Typography>
                        </Grid>
                    </Grid>}
                    {service && (disabled || customerInfo?.isInvoicingEnabled) &&
                        <Grid>
                            <Typography variant="h4">
                                {disabled ? t('selfService.paymentOngoing') : t('selfService.paymentViaInvoice')}
                            </Typography>
                        </Grid>
                    }
                </Grid>
            </Grid>
        }
    </>)
}

export default ShowServices
